import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import './TikTokFulfilment.css';
import LogisticsPageHero from '../Logistics/LogisticsPageHero';
import Newsletter from '../Homepage/Newsletter';
import Footer from '../Layouts/Footer';
import Tiktokfulfilmentcontent from './Tiktokfulfilmentcontent';
import Canonical from '../../seo/Canonical';
import ComparisonTable from '../Common/ComparisonTable';
import RelatedLinks from '../Common/RelatedLinks';
import PageLastUpdated from '../Common/PageLastUpdated';
import TikTokShopFAQs from './TikTokShopFAQs';
import {
  COMPANY_PICKING_ACCURACY,
  COMPANY_PICK_PACK_FROM,
  COMPANY_SHIPPING_FROM,
} from '../../seo/companyFacts';

const TikTokFulfilment: React.FC = () => {
  return (
    <div className="tiktok-fulfillment-page logistics-page">
      <Canonical baseUrl="https://www.pickpackpro.co.uk" />

      <Helmet>
        <script type="application/ld+json">
          {`{
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "TikTok Shop Fulfilment Services UK",
            "description": "TikTok Shop fulfilment UK is outsourced pick, pack and dispatch for TikTok Shop orders so viral spikes do not break your SLA.",
            "author": {
              "@type": "Organization",
              "name": "Pick Pack Pro"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Pick Pack Pro",
              "logo": {
                "@type": "ImageObject",
                "url": "https://www.pickpackpro.co.uk/Images/ppp-orange-Blue-logo.png"
              }
            },
            "datePublished": "2025-01-01T08:00:00Z",
            "dateModified": new Date().toISOString()
          }`}
        </script>
      </Helmet>

      <LogisticsPageHero
        ariaLabel="TikTok Shop fulfilment UK"
        imageSrc="/Images/TikTok-Fulfilment.webp"
        imageAlt="TikTok Shop fulfilment warehouse"
        titleLight="TikTok Shop Fulfilment Services UK"
        titleAccent="for Fast-Growing Ecommerce Brands"
        description={
          <>
            <PageLastUpdated
              path="/tiktok-shop-fulfillment-services"
              className="page-last-updated--on-dark page-last-updated--hero"
            />
            <p>
              TikTok Shop fulfilment UK is outsourced pick, pack and dispatch for TikTok Shop orders so viral spikes
              do not break your SLA. Pick Pack Pro syncs orders from{' '}
              <a href="https://seller-uk.tiktok.com/" target="_blank" rel="noopener noreferrer nofollow">
                TikTok Shop Seller Centre UK
              </a>
              , picks from Milton Keynes stock, and ships with a {COMPANY_PICKING_ACCURACY} accuracy target — pick and
              pack from {COMPANY_PICK_PACK_FROM}, tracked delivery from {COMPANY_SHIPPING_FROM}.
            </p>
            <p>
              Pair with{' '}
              <Link to="/tiktok-live-sales-fulfillment" className="tiktok-inline-link">
                TikTok Live fulfilment
              </Link>
              ,{' '}
              <Link to="/shopify-fulfillment-services" className="tiktok-inline-link">
                Shopify fulfilment UK
              </Link>
              , or{' '}
              <Link to="/multi-channel-fulfillment-services" className="tiktok-inline-link">
                multi-channel fulfilment
              </Link>{' '}
              from the same{' '}
              <Link to="/our-fulfilment-center" className="tiktok-inline-link">
                UK fulfilment centre
              </Link>
              .
            </p>
          </>
        }
        features={[
          'Picking Fees from £0.99',
          'Next-Day Delivery from £1.99',
          '99.9% Order Accuracy',
          'Dedicated Account Manager',
          'Price Match Promise',
        ]}
        primaryCta={{ label: 'Get a Custom Quote', to: '/contact-fulfillment-services' }}
      />

      
      {/* AEO direct-answer section */}
      <section
        className="tiktok-aeo-section"
        aria-labelledby="tiktok-aeo-title"
        style={{ background: '#f8fafc', padding: '4rem 2rem' }}
      >
        <div style={{ maxWidth: '900px', margin: '0 auto', background: '#ffffff', padding: '3rem', borderRadius: '16px', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.05)', borderLeft: '5px solid #FE2C55' }}>
          <h2 id="tiktok-aeo-title" style={{ fontSize: '1.8rem', fontWeight: '800', color: '#1a202c', marginBottom: '1.5rem' }}>
            What is TikTok Shop Fulfilment?
          </h2>
          <p style={{ fontSize: '1.1rem', lineHeight: '1.8', color: '#4a5568', marginBottom: '1.5rem' }}>
            <strong>Definition:</strong> TikTok Shop fulfilment is the process of storing, picking, packing, and dispatching orders originating from the TikTok Seller Centre directly to consumers. It ensures rapid delivery and accurate inventory syncing so viral sales spikes do not break seller SLAs. Pick Pack Pro is a UK-based 3PL and Amazon FBA prep centre operating from Milton Keynes, supporting Amazon, Shopify, TikTok Shop and multi-channel sellers.
          </p>
          <p style={{ fontSize: '1.1rem', lineHeight: '1.8', color: '#4a5568', margin: 0 }}>
            <strong>Audience & Use-Case:</strong> This service is designed for fast-growing UK ecommerce brands, viral creators, and multi-channel sellers who experience unpredictable order volumes. You should use outsourced TikTok fulfilment when you need to maintain 24-hour dispatch SLAs during live stream sales without hiring temporary warehouse staff.
          </p>
        </div>
      </section>

      <ComparisonTable
        title="TikTok Shop Fulfilment: Pick Pack Pro vs In-House Operations"
        subtitle={
          <>
            Compare our TikTok Shop fulfilment services with managing orders in-house and see why growing ecommerce
            brands choose outsourced fulfilment. View transparent{' '}
            <Link to="/pricing-fulfilment-services" className="tiktok-inline-link">
              fulfilment pricing UK
            </Link>{' '}
            before you switch.
          </>
        }
        leftLabel="Pick Pack Pro"
        rightLabel="In-House Fulfilment"
        rows={[
          {
            feature: 'Viral Order Spikes',
            pickPackPro:
              'Scalable TikTok Shop fulfilment built to absorb viral demand with flexible labour and batch processing.',
            alternative: 'Order surges quickly overwhelm warehouse capacity and slow dispatch.',
          },
          {
            feature: 'Tracking & Compliance',
            pickPackPro:
              'Fast carrier tracking uploads and compliant dispatch protect TikTok Shop seller performance.',
            alternative: 'Delayed labels and tracking updates can affect seller metrics.',
          },
          {
            feature: 'Inventory Management',
            pickPackPro:
              'Real-time inventory management, cycle counts, and audit trails reduce overselling.',
            alternative: 'Stock discrepancies become common across multiple sales channels.',
          },
          {
            feature: 'Dispatch Speed',
            pickPackPro: 'Same-day dispatch within agreed cut-off times for faster order fulfilment.',
            alternative: 'Dispatch performance depends on staffing levels and operational capacity.',
          },
          {
            feature: 'Customer Experience',
            pickPackPro:
              'Accurate pick and pack fulfilment with fewer errors, returns, and refund requests.',
            alternative: 'Error rates typically increase as order volumes grow.',
          },
          {
            feature: 'Business Growth',
            pickPackPro:
              'Focus on marketing, product launches, and TikTok content while we manage fulfilment.',
            alternative: 'Daily fulfilment operations consume valuable business time.',
          },
        ]}
      />
      <Tiktokfulfilmentcontent />

      <TikTokShopFAQs />

      <RelatedLinks
        serviceLinks={[
          { to: '/multi-channel-fulfillment-services', label: 'Multi-channel fulfilment services' },
          { to: '/shopify-fulfillment-services', label: 'Shopify fulfilment services UK' },
          { to: '/blog/tiktok-shop-fulfilment-that-can-keep-up', label: 'TikTok Shop fulfilment that can keep up' },
        ]}
        blogLink={{ to: '/blog/tiktok-shop-fulfilment-uk', label: 'TikTok Shop fulfilment UK guide' }}
      />

      <Newsletter />
      <Footer />
    </div>
  );
};

export default TikTokFulfilment;
